/**
 * Pure-function parser for the GoPhotoScan input pipeline.
 *
 * Extracted from GoPhotoScan.vue so the classify → extract → validate
 * pipeline can be fuzz-tested without mounting the component (the Vue
 * mount + jsdom round-trip is slow and noisy for 1k+ iteration random
 * input coverage).
 *
 * Three explicit steps; each one is pure and never throws:
 *   - classifyInput  — shape detection (empty / url / gophoto-url / manual)
 *   - extractCode    — strict_url / loose_url / manual / null strategies
 *   - validateCode   — exact 6 alphanum (mirror of backend Pydantic Field)
 *
 * Why three steps instead of one big regex: it lets us telemeter the
 * exact step on which a partner's traffic stops working. See the PR
 * description for the Sentry counter names tied to each branch.
 */

// Canonical PhotoPass code shape (mirrors backend
// SetGoPhotoCodeRequest.gophoto_code Field(min/max_length=6, pattern=...)).
export const CODE_PATTERN = /^[A-Za-z0-9]{6}$/;

// Strict URL pattern: the format GoPhoto has historically printed on
// PhotoPass tickets — gophoto.pro or gophoto.com followed by exactly a
// 6-char alphanum code as the first path segment.
export const STRICT_GOPHOTO_URL = /gophoto\.(?:pro|com)\/([A-Za-z0-9]{6})/i;

/**
 * Structural fingerprint of raw input for Sentry breadcrumbs. Never
 * emits the full code — only length + first/last char + a few shape
 * booleans, so even if a breadcrumb leaks (in a captured exception
 * extra payload, replay, etc.) no usable PhotoPass credential travels
 * with it.
 *
 * @param {string} raw
 * @returns {object}
 */
export function inputFingerprint(raw) {
  if (!raw) return { length: 0 };
  const trimmed = raw.trim();
  return {
    length: trimmed.length,
    firstChar: trimmed.charAt(0) || null,
    lastChar: trimmed.length > 1 ? trimmed.charAt(trimmed.length - 1) : null,
    containsSlash: trimmed.includes('/'),
    containsDot: trimmed.includes('.'),
    containsGophoto: /gophoto\./i.test(trimmed),
    looksLikeUrl: /^https?:\/\//i.test(trimmed),
  };
}

/**
 * Hostname for breadcrumb diagnostics. Hostnames are not PII (they
 * identify the partner's domain, not the user). Returns null if the
 * input is not a parseable URL.
 *
 * @param {string} raw
 * @returns {string | null}
 */
export function safeHostname(raw) {
  try {
    return new URL(raw.trim()).hostname || null;
  } catch {
    return null;
  }
}

/**
 * Pull the LAST non-empty path segment out of a URL, sanitize to upper
 * alphanum, and return it if it ends up exactly 6 chars. This is the
 * most-liberal-but-still-validatable extractor:
 *
 *   https://gophoto.pro/N55X6V                       → "N55X6V"
 *   https://gophoto.pro/p/N55X6V                     → "N55X6V"
 *   https://gophoto.pro/photopass/N55X6V?utm=x       → "N55X6V"
 *   https://tickets.example.com/scan/N55X6V#hash     → "N55X6V"
 *   https://gophoto.pro/                             → null (no segment)
 *   https://gophoto.pro/scan/N55X6V/extra            → null (last seg too long)
 *   not-a-url                                        → null (URL throws)
 *
 * Backend Pydantic still enforces exact 6 alphanum, so even if this
 * function gets even more liberal in the future the DB can't ingest
 * garbage codes. The reason to constrain to 6 alphanum HERE is to
 * avoid a chatty UI failure mode where a wrong-length candidate is
 * submitted and rejected by the API for every scan.
 *
 * @param {string} raw
 * @returns {string | null}
 */
export function extractCodeFromUrlPath(raw) {
  try {
    const url = new URL(raw.trim());
    const segments = url.pathname.split('/').filter(Boolean);
    if (segments.length === 0) return null;
    const last = segments[segments.length - 1];
    const sanitized = last.toUpperCase().replace(/[^A-Z0-9]/g, '');
    return sanitized.length === 6 ? sanitized : null;
  } catch {
    return null;
  }
}

/**
 * Step 1 — classify input shape.
 *
 * @param {string} input — already trimmed
 * @returns {{ type: 'empty' | 'gophoto_url_strict' | 'gophoto_url_malformed' | 'unknown_url' | 'manual_code' }}
 */
export function classifyInput(input) {
  if (!input) return { type: 'empty' };

  const looksLikeUrl = /^https?:\/\//i.test(input);
  const containsGophoto = /gophoto\./i.test(input);

  if (looksLikeUrl && containsGophoto) {
    return {
      type: STRICT_GOPHOTO_URL.test(input)
        ? 'gophoto_url_strict'
        : 'gophoto_url_malformed',
    };
  }
  if (looksLikeUrl) {
    return { type: 'unknown_url' };
  }
  return { type: 'manual_code' };
}

/**
 * Step 2 — try to extract a candidate code.
 *
 * Strategies are attempted in order; the first to yield a 6-alphanum
 * code wins. Returned `strategy` identifies which one fired, so a
 * Sentry counter per strategy tells us at a glance how partners'
 * traffic is distributing across "canonical GoPhoto URL", "different
 * partner domain or path layout", "manual keypad entry".
 *
 *   strict_url — gophoto.(pro|com)/CODE  (canonical)
 *   loose_url  — any URL whose last path segment sanitizes to 6 alphanum
 *   manual     — input itself is 6 alphanum after sanitization
 *
 * @param {string} input — already trimmed
 * @param {{ type: string }} classification — output of classifyInput
 * @returns {{ code: string | null, strategy: 'strict_url' | 'loose_url' | 'manual' | null, hostname: string | null }}
 */
export function extractCode(input, classification) {
  const strict = input.match(STRICT_GOPHOTO_URL);
  if (strict) {
    return {
      code: strict[1].toUpperCase(),
      strategy: 'strict_url',
      hostname: safeHostname(input),
    };
  }

  if (
    classification.type === 'unknown_url' ||
    classification.type === 'gophoto_url_malformed'
  ) {
    const looseCode = extractCodeFromUrlPath(input);
    if (looseCode) {
      return {
        code: looseCode,
        strategy: 'loose_url',
        hostname: safeHostname(input),
      };
    }
  }

  if (classification.type === 'manual_code') {
    const sanitized = input.toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (sanitized.length === 6) {
      return { code: sanitized, strategy: 'manual', hostname: null };
    }
  }

  return { code: null, strategy: null, hostname: safeHostname(input) };
}

/**
 * Step 3 — validate candidate. Mirrors CODE_PATTERN and is kept as a
 * separate step so a future loosening of extractCode can't silently
 * bypass the 6-alphanum invariant the backend enforces.
 *
 * @param {string | null | undefined} candidate
 * @returns {{ valid: boolean, reason: 'empty' | 'too_short' | 'too_long' | 'non_alphanum' | null }}
 */
export function validateCode(candidate) {
  if (!candidate) return { valid: false, reason: 'empty' };
  if (candidate.length !== 6) {
    return {
      valid: false,
      reason: candidate.length < 6 ? 'too_short' : 'too_long',
    };
  }
  if (!CODE_PATTERN.test(candidate)) {
    return { valid: false, reason: 'non_alphanum' };
  }
  return { valid: true, reason: null };
}
